// write a screen map for perch energy, similar to solar-funnel/screen-map.tsx

import { Loading } from '@arcadiapower/shrike';
import { AccountNumberContainer } from '~/screens/account-number/account-number.container';
import { BillUploadContainer } from '~/screens/bill-upload/bill-upload.container';
import { CreateAccountGoogleContainer } from '~/screens/create-account-google/create-account-google.container';
import { CreateAccountContainer } from '~/screens/create-account/create-account.container';
import { CredentialsContainer } from '~/screens/credentials/credentials.container';
import { CsUnavailableContainer } from '~/screens/cs-unavailable/cs-unavailable.container';
import { Friction, Unexpected } from '~/screens/errors/errors.component';
import { LMIProofDocsUploadContainer } from '~/screens/lmi-proof-docs-upload/lmi-proof-docs-upload.container';
import { SelectUtilityContainer } from '~/screens/select-utility/select-utility.container';
import { EnrollmentMachine } from '../enrollment-selection';
import { UploadMachine } from '../upload';
import { UserMachine } from '../user-creation';
import { UtilityMachine } from '../utility-selection';
import { MachineScreenStates, ScreenMap } from '../utils/types';
import { PerchEnergyMachine } from './defs/types';

export type PerchEnergyScreenMap = {
  utility: ScreenMap<PerchEnergyMachine, UtilityMachine>;
  user: Omit<ScreenMap<PerchEnergyMachine, UserMachine>, 'microLearning'>;
  upload: ScreenMap<PerchEnergyMachine, UploadMachine>;
  fetchFeatureFlags: ScreenMap<PerchEnergyMachine>;
  enrollment: ScreenMap<PerchEnergyMachine, EnrollmentMachine>;
  completed: ScreenMap<PerchEnergyMachine>;
  updateRequireAffirmativeConsent: ScreenMap<PerchEnergyMachine>;
  getEnrollmentOptions: ScreenMap<PerchEnergyMachine>;
  unexpected: ScreenMap<PerchEnergyMachine>;
  csUnavailable: ScreenMap<PerchEnergyMachine>;
};
export const perchEnergyScreenMap: PerchEnergyScreenMap = {
  utility: {
    utility: SelectUtilityContainer,
    invalidProjectId: () => {
      return (
        <CsUnavailableContainer
          heading="Sorry, The project you selected is not available"
          subHeading="In the meantime explore our site to learn more about community solar."
        />
      );
    },
    bouncer: () => <Loading />,
    friction: () => <Friction />,
    redirect: () => <Loading />,
    completed: () => <Loading />,
  },

  fetchFeatureFlags: () => <Loading />,
  user: {
    user: CreateAccountContainer,
    google: CreateAccountGoogleContainer,
    completed: () => <Loading />,
  },
  enrollment: {
    utilityConnect: CredentialsContainer,
    accountNumber: AccountNumberContainer,
    billUpload: BillUploadContainer,
    fallBackAccountNumber: ({ micro, overrideCopy, funnel }) => (
      <AccountNumberContainer
        micro={micro}
        funnel={funnel}
        overrideCopy={overrideCopy}
        overrideProps={{
          error: micro.context.errors.fallBackAccountNumber,
          pending: micro.matches('fallBackAccountNumber.submitting'),
        }}
      />
    ),
    nyLmiZoneOptions: () => <Loading />,
    isInLmiZone: () => <Loading />,
    completed: () => <Loading />,
    ameren: () => <Loading />,
    amerenAuthTimeout: () => <Loading />,
  },
  getEnrollmentOptions: () => <Loading />,
  updateRequireAffirmativeConsent: () => <Loading />,
  unexpected: () => <Unexpected />,
  upload: {
    idle: LMIProofDocsUploadContainer,
    submitting: LMIProofDocsUploadContainer,
    completed: () => <Loading />,
  },
  completed: () => <Loading />,
  csUnavailable: () => <CsUnavailableContainer />,
};

export const _completenessCheck: Record<
  MachineScreenStates<PerchEnergyMachine>,
  any
> = perchEnergyScreenMap;
